import {Modal, StyleSheet, Text, TextInput} from "react-native";
import {EditableGroup, EditGroupRequest} from "@/lib/types";
import {useLocalSearchParams, useRouter} from "expo-router";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/pages/Page";
import Card from "@/lib/components/Card";
import ErrorPage from "@/lib/pages/ErrorPage";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import SpinnerPage from "@/lib/pages/SpinnerPage";
import {Color} from "@/lib/styles";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import {useEffect, useMemo, useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Editable from "@/lib/components/Editable";
import Center from "@/lib/components/Center";
import Colorpicker from "@/lib/components/Colorpicker";
import {colorName} from "@/lib/color";
import Header from "@/lib/components/Header";
import Radio from "@/lib/components/Radio";
import globstyles from "@/lib/globstyles";

type EditableComponent =
    null
    | "color"
    | "name"
    | "info"
    | "visibility"
    | "permissions";

export default function GroupPage() {
    // Start editing this component
    const [isEditing, setIsEditing] = useState<EditableComponent>(null);

    const {group_id} = useLocalSearchParams();
    const {session} = useSession();
    const router = useRouter();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState(false);
    const [saveError, setSaveError] = useState<Error | null>(null);

    const [originalDraft, setOriginalDraft] = useState<EditGroupRequest | null>(null);
    const [draft, setDraft] = useState<EditGroupRequest | null>(null);
    const [notify, setNotify] = useState(false);

    const {data, isPending, error} = useQuery<EditableGroup>({
        queryKey: ['group', group_id, 'edit', session],
        queryFn: () => fetchBackend(`/group/${group_id}/edit`, "GET", session),
        retry: dataRetryPolicy,
    })

    useEffect(() => {
        if (data) {
            setOriginalDraft(data.group);
            setDraft(data.group);
        }
    }, [data])

    const colorNameMemo = useMemo(() => {
        return colorName(data?.group.color!);
    }, [data?.group.color])

    const infoWordCount = useMemo(() => {
            return data?.group.info?.split(/\s+/).length || 0;
        }, [data?.group.info]
    )

    const permissionsMemo = useMemo(() => {
        const flags: string[] = [];
        if (data?.group.open_invite) {
            flags.push("Alle kan invitere");
        }
        if (data?.group.open_posting) {
            flags.push("Alle kan starte diskusjon");
        }
        if (flags.length === 0) {
            return "Ingen";
        }
        return flags.join("\n");
    }, [data]);

    if (error) {
        return <ErrorPage error={error}/>
    } else if (isPending || !data || !draft) {
        return <SpinnerPage/>
    }

    const saveAndPublish = async () => {
        setBusy(true);
        const merged: EditGroupRequest = {...draft, ...{publish: true, notify}};
        fetchBackendNoContent(`/group/${data.group.group_id}`, "PATCH", session, merged).then(async () => {
            await queryClient.invalidateQueries({queryKey: ['group', group_id, 'edit', session]});
            await queryClient.invalidateQueries({queryKey: ['group', group_id, session]});
            await queryClient.invalidateQueries({queryKey: ['group', session]});
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    const saveDraft = async (changes?: EditGroupRequest) => {
        setSaveError(null);
        setBusy(true);
        const merged = {...draft, ...changes};
        setDraft(merged);
        fetchBackendNoContent(`/group/${data.group.group_id}`, "PATCH", session, merged).then(async () => {
            await queryClient.invalidateQueries({queryKey: ['group', group_id, 'edit', session]});
            setIsEditing(null);
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setIsEditing(null);
            setBusy(false);
        });
    }

    function storeInDraft(updates: EditGroupRequest) {
        const merged: EditGroupRequest = {...draft, ...updates};
        setDraft(merged);
    }

    function startEdit(component: EditableComponent) {
        setSaveError(null);
        setIsEditing(component);
    }

    async function cancelEdit() {
        setDraft(originalDraft);
        setIsEditing(null);
    }

    return (
        <Page scrollable={true}>
            <Card
                image_url={data.group.image_url}
                color={data.group.color}
                contrast_color={data.group.contrast_color}
                onEditTitle={() => startEdit("name")}
                onEditImage={() => router.push(`/s/${data.group.group_id}/banner`)}
                title={draft.name!}
            >
                <Editable color={data.group.color} onPress={() => startEdit("color")}>
                    <Text style={styles.editPart}>Farge</Text>
                    <Text style={styles.editValue}>{colorNameMemo}</Text>
                </Editable>
                <Editable color={data.group.color} onPress={() => startEdit("visibility")}>
                    <Text style={styles.editPart}>Synlighet</Text>
                    <Text style={styles.editValue}>{data.group.public ? "Offentlig" : "Privat"}</Text>
                </Editable>
                <Editable color={data.group.color} onPress={() => startEdit("permissions")}>
                    <Text style={styles.editPart}>Tilganger</Text>
                    <Text style={styles.editValue}>{permissionsMemo}</Text>
                </Editable>
                <Editable color={data.group.color} onPress={() => startEdit("info")}>
                    <Text style={styles.editPart}>Info</Text>
                    {infoWordCount > 0 && <Text style={styles.editValue}>{infoWordCount} ord</Text>}
                    {infoWordCount === 0 && <Text style={styles.editValue}>–</Text>}
                </Editable>
            </Card>

            <Radio title={"Send push-varsler"}
                   description={"Kryss av for å sende ut push-varsel til alle medlemmer."}
                   selected={notify}
                   checkbox={true}
                   onPress={() => setNotify(!notify)}
            />

            {saveError && <ErrorMessage error={saveError}/>}

            <Nav>
                <ActionButton
                    icon={"checkmark"}
                    title={"Lagre og publiser"}
                    onPress={saveAndPublish}
                    busy={busy}
                    color={Color.Green}
                />
            </Nav>

            <Modal onRequestClose={cancelEdit}
                   visible={!!isEditing}
                   allowSwipeDismissal={true}
                   presentationStyle={"pageSheet"}
                   animationType={"slide"}
            >
                <Page>
                    {isEditing === "color" &&
                        <Center>
                            <Header title={"Velg farge"} center={true}/>
                            <Colorpicker color={data.group.color} onChange={async (color) => {
                                await saveDraft({color: color.color})
                            }}/>
                            <Nav>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "visibility" &&
                        <Center>
                            <Header title={"Synlighet"} center={true}/>
                            <Radio
                                title={"Åpent for alle"}
                                description={"Offentlig samfunn. Alle kan bli med. Synlig for søkemotorer og Internett."}
                                checkbox={false}
                                selected={draft.public === true}
                                onPress={() => {
                                    storeInDraft({public: true});
                                }}
                            />
                            <Radio
                                title={"Privat"}
                                description={"Krev invitasjon for å vise og delta i gruppen."}
                                checkbox={false}
                                selected={draft.public === false}
                                onPress={() => {
                                    storeInDraft({public: false});
                                }}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "permissions" &&
                        <Center>
                            <Header title={"Tilganger"} center={true}/>
                            <Radio
                                title={"Alle kan invitere"}
                                description={"Alle medlemmer kan invitere sine egne venner."}
                                checkbox={true}
                                selected={!!draft.open_invite}
                                onPress={() => {
                                    storeInDraft({open_invite: !draft?.open_invite});
                                }}
                            />
                            <Radio
                                title={"Alle kan starte diskusjon"}
                                description={"Alle medlemmer kan starte nye diskusjonstråder."}
                                checkbox={true}
                                selected={!!draft.open_posting}
                                onPress={() => {
                                    storeInDraft({open_posting: !draft?.open_posting});
                                }}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "name" &&
                        <Center>
                            <Header title={"Samfunnets navn"} center={true}/>
                            <TextInput
                                style={[
                                    styles.textInput,
                                    globstyles.cardTitle,
                                    {
                                        color: data.group.contrast_color,
                                        backgroundColor: data.group.color,
                                        borderWidth: 0,
                                    },
                                ]}
                                value={draft.name || ""}
                                onSubmitEditing={async () => {
                                    await saveDraft();
                                }}
                                onChangeText={(text) => {
                                    storeInDraft({name: text});
                                }}
                                autoFocus={true}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "info" &&
                        <Center style={{flex: 1, paddingVertical: 64}}>
                            <Header title={"Lang beskrivelse"} center={true}/>
                            <TextInput
                                style={[
                                    styles.textArea,
                                    {borderColor: data.group.color},
                                ]}
                                value={draft.info || ""}
                                multiline={true}
                                textAlignVertical={"top"}
                                onSubmitEditing={async () => {
                                    await saveDraft();
                                }}
                                onChangeText={(text) => {
                                    storeInDraft({info: text});
                                }}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                </Page>
            </Modal>
        </Page>
    )
}

export const styles = StyleSheet.create({
    text: {
        color: Color.PrimaryText,
        fontSize: 16,
        lineHeight: 26,
        width: "100%",
    },
    editPart: {
        color: Color.PrimaryText,
        fontSize: 16,
        fontWeight: 600,
    },
    editValue: {
        color: Color.PrimaryText,
        fontSize: 16,
    },
    textInput: {
        width: "100%",
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: Color.Yellow,
        borderRadius: 7,
        padding: 12,
        fontSize: 16,
        color: Color.PrimaryText,
        marginBottom: 8,
    },
    textArea: {
        width: "100%",
        flex: 1,
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: Color.Yellow,
        borderRadius: 7,
        textAlignVertical: "top",
        padding: 8,
        fontSize: 16,
        color: Color.PrimaryText,
        marginBottom: 8,
    },
    datePicker: {
        color: Color.PrimaryText,
    }
})